import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authed, object = authed
  const [user, setUser] = useState(null);

  const check = useCallback(async () => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check
    // so AuthCallback can exchange the session_id and establish the cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  // Admin (password) login — unchanged.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) localStorage.setItem("access_token", data.access_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  // Reader (Google via Emergent) — kick off OAuth.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Exchange session_id (from URL hash) for a persistent session cookie.
  const completeReaderSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", null, {
      headers: { "X-Session-ID": sessionId },
    });
    setUser({
      id: data.user_id,
      email: data.email,
      name: data.name,
      picture: data.picture,
      role: data.role,
    });
    return data;
  };

  const logout = async () => {
    const isReader = user && user.role === "reader";
    try {
      if (isReader) await api.post("/auth/reader/logout");
      else await api.post("/auth/logout");
    } catch { /* noop */ }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        signInWithGoogle,
        completeReaderSession,
        logout,
        refresh: check,
        formatApiError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
